import { Edit } from '@mui/icons-material'
import { Button, Card, CardContent, CircularProgress, Divider, Grid, Typography } from '@mui/material'
import { ExplanationAccordion, LabelField } from 'components'
import * as controllers from 'controllers'
import { PersonDriverInformationDto, PersonDto } from 'dtos'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import DriverInformationAddEditDialog from './DriverInformationAddEditDialog'
import { useAuth } from 'context'

interface DriverInformationCardProps {
  person: PersonDto
}

export default function DriverInformationCard({ person }: DriverInformationCardProps) {
  const { permissions } = useAuth()

  const [driverInformationAddEditDialogOpen, setDriverInformationAddEditDialogOpen] = useState<boolean>(false)
  const [isGettingPersonDriverInformation, setIsGettingPersonDriverInformation] = useState<boolean>(false)
  const [isSavingPersonDriverInformation, setIsSavingPersonDriverInformation] = useState<boolean>(false)
  const [personDriverInformation, setPersonDriverInformation] = useState<PersonDriverInformationDto>(
    new PersonDriverInformationDto()
  )

  useEffect(() => {
    if (person.personGuid) {
      setIsGettingPersonDriverInformation(true)
      controllers
        .getPersonDriverInformation(person.personGuid)
        .then(response => {
          setPersonDriverInformation(response.value)
        })
        .finally(() => {
          setIsGettingPersonDriverInformation(false)
        })
    }
  }, [person])

  return (
    <>
      {permissions.COMPANIES_AND_PEOPLE_ADD_EDIT && (
        <DriverInformationAddEditDialog
          initialValues={personDriverInformation}
          loading={isSavingPersonDriverInformation}
          onClose={() => {
            setDriverInformationAddEditDialogOpen(false)
          }}
          onSave={values => {
            setIsSavingPersonDriverInformation(true)
            controllers
              .updatePersonDriverInformation(person.personGuid!, values)
              .then(response => {
                enqueueSnackbar('Driver Information Saved Successfully!', { variant: 'success' })
                setPersonDriverInformation(response.value)
                setDriverInformationAddEditDialogOpen(false)
              })
              .finally(() => {
                setIsSavingPersonDriverInformation(false)
              })
          }}
          open={driverInformationAddEditDialogOpen}
        />
      )}

      <Card>
        <CardContent>
          <Grid container spacing={1} alignItems='flex-start'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm='auto'>
                <Typography fontWeight='bold' variant='h5'>
                  Driver Information
                </Typography>
              </Grid>

              <Grid item xs={12} sm='auto'>
                {permissions.COMPANIES_AND_PEOPLE_ADD_EDIT && (
                  <Button
                    endIcon={<Edit />}
                    onClick={() => {
                      setDriverInformationAddEditDialogOpen(true)
                    }}
                    variant='text'
                  >
                    EDIT
                  </Button>
                )}
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {isGettingPersonDriverInformation && (
              <Grid item container alignItems='center' justifyContent='center' sx={{ mt: 1 }}>
                <Grid item>
                  <CircularProgress color='primary' size={50} />
                </Grid>
              </Grid>
            )}

            {!isGettingPersonDriverInformation && (
              <>
                <Grid item xs={12}>
                  <LabelField label='Crew Member' value={personDriverInformation.driverCrewMember} />
                </Grid>

                <Grid item xs={12}>
                  <LabelField label='Vehicle Plate' value={personDriverInformation.driverVehiclePlate} />
                </Grid>

                <Grid item xs={12}>
                  <LabelField label='Vehicle Country' value={personDriverInformation.driverVehicleCountry} />
                </Grid>

                <Grid item xs={12}>
                  <LabelField label='Vehicle State' value={personDriverInformation.driverVehicleState} />
                </Grid>

                <Grid item xs={12}>
                  <LabelField label='Trailer Plate' value={personDriverInformation.driverTrailerPlate} />
                </Grid>

                <Grid item xs={12}>
                  <LabelField label='Trailer Country' value={personDriverInformation.driverTrailerCountry} />
                </Grid>

                <Grid item xs={12}>
                  <LabelField label='Trailer State' value={personDriverInformation.driverTrailerState} />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <ExplanationAccordion variant='outlined'>
                || This card only shows if person is marked as Driver. ||
              </ExplanationAccordion>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
